/** @jsxImportSource @emotion/react */
"use client";

import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import type { RefObject } from "react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { spacing } from "../../../../../../global/emotion";
import { CAROUSEL_GRID_SIZES } from "../../../../../constants/carouselGridSizes.constants";
import type { CarouselApi } from "../../../../../hooks/useCarousel";
import { useIntersectionObserverRefState } from "../../../../../hooks/useIntersectionObserverRefState";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import type { CarouselGridSize } from "../../../../../types/carouselGridSize.types";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";

const confinedCssMediaMinWidth1 =
  "@media (min-width: 640px) and (max-width: 767px)";
const confinedCssMediaMinWidth2 =
  "@media (min-width: 768px) and (max-width: 1023px)";
const confinedCssMediaMinWidth3 =
  "@media (min-width: 1024px) and (max-width: 1263px)";
const confinedCssMediaMinWidth4 =
  "@media (min-width: 1264px) and (max-width: 1535px)";
const confinedCssMediaMinWidth5 = "@media (min-width: 1536px)";

const hallowElement: HallowElement = "div";

const getPositionStyles = ({
  gap,
  isEnd,
  isStart,
}: {
  gap: CarouselApi["gap"];
  isEnd: boolean;
  isStart: boolean;
}) => {
  if (isEnd && isStart) return null;
  if (isEnd) return stylesPosition.end({ gap });
  if (isStart) return stylesPosition.start({ gap });
  return stylesPosition.base({ gap });
};

const determinedElementStyle: Interpolation = {
  flexShrink: 0,
  height: spacing.full,
  minWidth: spacing.none,
  scrollSnapAlign: "start",
};

const stylesPosition: Record<
  string,
  FunctionInterpolation<{ gap: CarouselApi["gap"] }>
> = {
  base: (props) => ({
    marginLeft: `${props.gap[0]}px`,
    marginRight: `${props.gap[0]}px`,
    [cssMediaQueryMinWidthTablet]: {
      marginLeft: `${props.gap[1]}px`,
      marginRight: `${props.gap[1]}px`,
    },
  }),
  end: (props) => ({
    marginLeft: `${props.gap[0]}px`,
    [cssMediaQueryMinWidthTablet]: { marginLeft: `${props.gap[1]}px` },
  }),
  start: (props) => ({
    marginRight: `${props.gap[0]}px`,
    [cssMediaQueryMinWidthTablet]: { marginRight: `${props.gap[1]}px` },
  }),
};

/* ((100% - gap * (#columns -1)) / #columns) */
const widthSizeStyle: Interpolation<{
  gap: CarouselApi["gap"];
  size: CarouselGridSize;
}> = (props) => ({
  width:
    props.size[0] === 1
      ? spacing.full
      : `calc(calc(100% - calc(${props.gap[0]}px * ${props.size[0] - 1})) / ${props.size[0]})`,
  [confinedCssMediaMinWidth1]: {
    width:
      props.size[1] === 1
        ? spacing.full
        : `calc(calc(100% - calc(${props.gap[0]}px * ${props.size[1] - 1})) / ${props.size[1]})`,
  },
  [confinedCssMediaMinWidth2]: {
    width:
      props.size[2] === 1
        ? spacing.full
        : `calc(calc(100% - calc(${props.gap[1]}px * ${props.size[2] - 1})) / ${props.size[2]})`,
  },
  [confinedCssMediaMinWidth3]: {
    width:
      props.size[3] === 1
        ? spacing.full
        : `calc(calc(100% - calc(${props.gap[1]}px * ${props.size[3] - 1})) / ${props.size[3]})`,
  },
  [confinedCssMediaMinWidth4]: {
    width:
      props.size[4] === 1
        ? spacing.full
        : `calc(calc(100% - calc(${props.gap[1]}px * ${props.size[4] - 1})) / ${props.size[4]})`,
  },
  [confinedCssMediaMinWidth5]: {
    width:
      props.size[5] === 1
        ? spacing.full
        : `calc(calc(100% - calc(${props.gap[1]}px * ${props.size[5] - 1})) / ${props.size[5]})`,
  },
});

/* (100% - (offset1 + offset2 + ((2 * (#columns - 1)) * gap))) / #columns) */
const widthOffsetStyle: Interpolation<{
  gap: CarouselApi["gap"];
  pageOffset: CarouselApi["pageOffset"];
  size: CarouselGridSize;
}> = (props) => ({
  width:
    props.size[0] === 1
      ? `calc(100% - calc(2 * ${(props.pageOffset[0][0] + props.pageOffset[1][0]) / 2}px))`
      : `calc(calc(100% - ${props.pageOffset[0][0] + props.pageOffset[1][0] + 2 * (props.size[0] - 1) * props.gap[0]}px) / ${props.size[0]})`,
  [confinedCssMediaMinWidth1]: {
    width:
      props.size[1] === 1
        ? `calc(100% - calc(2 * ${(props.pageOffset[0][0] + props.pageOffset[1][0]) / 2}px))`
        : `calc(calc(100% - ${props.pageOffset[0][0] + props.pageOffset[1][0] + 2 * (props.size[1] - 1) * props.gap[0]}px) / ${props.size[1]})`,
  },
  [confinedCssMediaMinWidth2]: {
    width:
      props.size[2] === 1
        ? `calc(100% - calc(2 * ${(props.pageOffset[0][1] + props.pageOffset[1][1]) / 2}px))`
        : `calc(calc(100% - ${props.pageOffset[0][1] + props.pageOffset[1][1] + 2 * (props.size[2] - 1) * props.gap[1]}px) / ${props.size[2]})`,
  },
  [confinedCssMediaMinWidth3]: {
    width:
      props.size[3] === 1
        ? `calc(100% - calc(2 * ${(props.pageOffset[0][1] + props.pageOffset[1][1]) / 2}px))`
        : `calc(calc(100% - ${props.pageOffset[0][1] + props.pageOffset[1][1] + 2 * (props.size[3] - 1) * props.gap[1]}px) / ${props.size[3]})`,
  },
  [confinedCssMediaMinWidth4]: {
    width:
      props.size[4] === 1
        ? `calc(100% - calc(2 * ${(props.pageOffset[0][1] + props.pageOffset[1][1]) / 2}px))`
        : `calc(calc(100% - ${props.pageOffset[0][1] + props.pageOffset[1][1] + 2 * (props.size[4] - 1) * props.gap[1]}px) / ${props.size[4]})`,
  },
  [confinedCssMediaMinWidth5]: {
    width:
      props.size[5] === 1
        ? `calc(100% - calc(2 * ${(props.pageOffset[0][1] + props.pageOffset[1][1]) / 2}px))`
        : `calc(calc(100% - ${props.pageOffset[0][1] + props.pageOffset[1][1] + 2 * (props.size[5] - 1) * props.gap[1]}px) / ${props.size[5]})`,
  },
});

export type CarouselItemProps = HallowElementProps<typeof hallowElement> & {
  carouselApi: CarouselApi;
  isEnd?: boolean;
  isStart?: boolean;
  visibilityProps?: {
    onVisible: () => void;
    viewportRef: RefObject<HTMLElement>;
    observerProps?: IntersectionObserverInit;
  };
};

export const CarouselItem = forwardRef<any, CarouselItemProps>(
  (
    {
      carouselApi,
      children,
      isEnd = false,
      isStart = false,
      visibilityProps,
      ...props
    },
    forwardedRef,
  ) => {
    const { gap, pageOffset, size, fixedWidth, forceMount } = carouselApi;

    const ref = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);
    const [isVisible] = useIntersectionObserverRefState({
      deps: [pageOffset, size],
      ref,
      rootRef: carouselApi.refParent,
      continuous: false,
    });
    const [isCurrentlyVisible, setIsCurrentlyVisible] =
      useState<boolean>(false);

    useEffect(() => {
      const obs = new IntersectionObserver(
        (intersectionEvents) => {
          for (const evt of intersectionEvents)
            setIsCurrentlyVisible(evt.isIntersecting);
        },
        {
          root: visibilityProps?.viewportRef?.current,
          threshold: [1],
          ...(visibilityProps?.observerProps ?? {}),
        },
      );

      if (ref?.current) obs.observe(ref.current);

      return () => {
        obs.disconnect();
      };
    }, [visibilityProps]);

    useEffect(() => {
      if (isCurrentlyVisible && visibilityProps) visibilityProps.onVisible();
    }, [isCurrentlyVisible, visibilityProps]);

    const DeterminedElement = hallowElement;
    const determinedCarouselGridSize =
      CAROUSEL_GRID_SIZES[size as keyof typeof CAROUSEL_GRID_SIZES] ??
      (size as CarouselGridSize);

    return (
      <DeterminedElement
        ref={ref}
        css={[
          determinedElementStyle,
          getPositionStyles({ gap, isEnd, isStart }),
          fixedWidth &&
            (pageOffset
              ? widthOffsetStyle({
                  gap,
                  pageOffset: pageOffset,
                  size: determinedCarouselGridSize,
                })
              : widthSizeStyle({ gap, size: determinedCarouselGridSize })),
        ]}
        {...props}
      >
        {(isVisible || forceMount) && children}
      </DeterminedElement>
    );
  },
);

CarouselItem.displayName = "CarouselItem";
